import sentimentNegativeIcon from '~/assets/svgs/icon-sentiment-negative-black.svg?react'
import sentimentNeutralIcon from '~/assets/svgs/icon-sentiment-neutral-black.svg?react'
import sentimentPositiveIcon from '~/assets/svgs/icon-sentiment-positive-black.svg?react'
import { Sentiment } from '~/types/SentimentTypes'
import { FilterItem, FilterSection, SelectedFilterData } from '~/types/Shared'

import {
  CallDirection,
  CallFilterParams,
  CallsQueryParams,
  CallStatus,
  User,
} from '@mangovoice/types'

import { CallsRouteQueryParametersRaw } from '../CallsRoutingTypes'

export interface DirectionItem extends FilterItem {
  title: string & ('Inbound' | 'Outbound' | 'Internal')
  data: CallDirection
}

export interface OutcomeItem extends FilterItem {
  title: string & ('Answered' | 'Missed')
  data: boolean | undefined
}

export interface SentimentItem extends FilterItem {
  title: 'Positive' | 'Neutral' | 'Negative'
  data: Sentiment
}

export interface StatusItem extends FilterItem {
  title: string & ('Failed' | 'In Progress' | 'Completed' | 'Canceled' | 'Busy')
  data: CallsQueryParams['statuses']
}

export interface TraitsItem extends FilterItem {
  title: string & 'Externally Transfered'
  data: boolean | undefined
}

export interface DurationItem extends FilterItem {
  title: string & ('10 seconds' | '1 minute' | '5 minutes' | '10 minutes')
  data:
    | CallsQueryParams['duration_in_seconds_gte']
    | CallsQueryParams['duration_in_seconds_lt']
}

export interface CallTraitItem extends FilterItem {
  title: (string & 'Voicemail Recorded') | 'Externally Transferred'
  data: boolean | undefined
}

export interface UserItem extends FilterItem {
  title: string
  data: number
}

export const sectionIndices = {
  direction: 0,
  outcome: 1,
  sentiment: 2,
  statuses: 3,
  callTraits: 4,
  duration: 5,
  user: 6,
}

export const getDefaultFilterData = (
  callAnalyticsEnabled?: boolean,
): FilterSection[] => {
  const defaultFilterData = [
    {
      title: 'Direction',
      rows: [
        {
          items: [
            {
              title: 'Inbound',
              data: CallDirection.Inbound,
            },
            {
              title: 'Outbound',
              data: CallDirection.Outbound,
            },
            {
              title: 'Internal',
              data: CallDirection.Internal,
            },
          ] as DirectionItem[],
        },
      ],
    },
    {
      title: 'Outcome',
      rows: [
        {
          items: [
            {
              title: 'Missed',
              data: true,
            },
            {
              title: 'Answered',
              data: false,
            },
          ] as OutcomeItem[],
        },
      ],
    },

    {
      title: 'Sentiment',
      rows: [
        {
          title: 'Office',
          items: [
            {
              title: 'Negative',
              data: Sentiment.Negative,
              icon: sentimentNegativeIcon,
            },
            {
              title: 'Neutral',
              data: Sentiment.Neutral,
              icon: sentimentNeutralIcon,
            },
            {
              title: 'Positive',
              data: Sentiment.Positive,
              icon: sentimentPositiveIcon,
            },
          ] as SentimentItem[],
        },
        {
          title: 'Patient',
          items: [
            {
              title: 'Negative',
              data: Sentiment.Negative,
              icon: sentimentNegativeIcon,
            },
            {
              title: 'Neutral',
              data: Sentiment.Neutral,
              icon: sentimentNeutralIcon,
            },
            {
              title: 'Positive',
              data: Sentiment.Positive,
              icon: sentimentPositiveIcon,
            },
          ] as SentimentItem[],
        },
      ],
    },
    {
      title: 'Statuses',
      rows: [
        {
          items: [
            {
              title: 'In Progress',
              data: [CallStatus.InProgress],
            },
            {
              title: 'Completed',
              data: [CallStatus.Completed],
            },
            {
              title: 'Failed',
              data: [CallStatus.Failed],
            },
            {
              title: 'Canceled',
              data: [CallStatus.Canceled],
            },
            {
              title: 'Busy',
              data: [CallStatus.Busy],
            },
          ],
        },
      ],
    },
    {
      title: 'Call Traits',
      rows: [
        {
          items: [
            {
              title: 'Voicemail Recorded',
              data: true,
            },
            {
              title: 'Externally Transferred',
              data: true,
            },
          ] as CallTraitItem[],
        },
      ],
    },
    {
      title: 'Duration',
      rows: [
        {
          title: 'Longer Than',
          items: [
            {
              title: '10 seconds',
              data: 10,
            },
            {
              title: '1 minute',
              data: 60,
            },
            {
              title: '5 minutes',
              data: 300,
            },
            {
              title: '10 minutes',
              data: 600,
            },
          ] as DurationItem[],
        },
        {
          title: 'Shorter Than',
          items: [
            {
              title: '10 seconds',
              data: 10,
            },
            {
              title: '1 minute',
              data: 60,
            },
            {
              title: '5 minutes',
              data: 300,
            },
            {
              title: '10 minutes',
              data: 600,
            },
          ] as DurationItem[],
        },
      ],
    },
    {
      title: 'User',
      rows: [
        {
          items: [] as UserItem[],
        },
      ],
    },
  ]
  if (callAnalyticsEnabled) {
    return [...defaultFilterData]
  } else {
    return [
      ...defaultFilterData.filter((section) => section.title !== 'Sentiment'),
    ]
  }
}

export const tryGetQueryFilters = (
  selectedFilters: SelectedFilterData[] = [],
) => {
  const queryParams: CallFilterParams = {}

  selectedFilters.forEach((filter) => {
    const { sectionTitle, rowTitle, itemTitle, item } = filter

    switch (sectionTitle) {
      case 'Direction':
        {
          const directionItem = item as DirectionItem
          queryParams.directions = queryParams.directions ?? []
          queryParams.directions.push(directionItem.data)
        }
        break
      case 'Outcome':
        {
          const outcomeItem = item as OutcomeItem
          queryParams.is_missed = outcomeItem.data
        }
        break
      case 'Sentiment':
        {
          const sentimentRowTitle = rowTitle
          const sentimentItem = item as SentimentItem

          if (sentimentRowTitle === 'Office') {
            queryParams.office_overall_sentiment_score =
              queryParams.office_overall_sentiment_score ?? []
            queryParams.office_overall_sentiment_score.push(sentimentItem.data)
          } else if (sentimentRowTitle === 'Patient') {
            queryParams.customer_overall_sentiment_score =
              queryParams.customer_overall_sentiment_score ?? []
            queryParams.customer_overall_sentiment_score.push(
              sentimentItem.data,
            )
          }
        }
        break
      case 'Statuses':
        {
          const statusItem = item as StatusItem
          if (!statusItem.data) {
            return
          }
          queryParams.statuses = queryParams.statuses ?? []
          queryParams.statuses = [...queryParams.statuses, ...statusItem.data]
        }
        break
      case 'Call Traits':
        {
          const traitItem = item as TraitsItem
          switch (itemTitle) {
            case 'Voicemail Recorded':
              queryParams.has_voicemail = traitItem.data
              break
            case 'Externally Transferred':
              queryParams.is_externally_transferred = traitItem.data
              break
          }
        }
        break
      case 'Duration':
        {
          const durationItem = item as DurationItem
          if (rowTitle === 'Longer Than') {
            queryParams.duration_in_seconds_gte = durationItem.data
          } else {
            queryParams.duration_in_seconds_lt = durationItem.data
          }
        }
        break
      case 'User':
        {
          const userItem = item as UserItem
          queryParams.related_user_ids = queryParams.related_user_ids ?? []
          queryParams.related_user_ids.push(userItem.data)
        }
        break
      default:
        console.log(`Unknown Filter Section Title ${sectionTitle}`)
        break
    }
  })

  return queryParams
}
interface TryGetFilterDataFromQueryParamsParams {
  callsQueryParams: CallsRouteQueryParametersRaw
  callAnalyticsEnabled?: boolean
  pbxUsers?: User[]
}
export const tryGetFilterDataFromQueryParams = ({
  callsQueryParams,
  callAnalyticsEnabled,
  pbxUsers,
}: TryGetFilterDataFromQueryParamsParams) => {
  const filterData = getDefaultFilterData(callAnalyticsEnabled)

  filterData.forEach((section) => {
    switch (section.title) {
      case 'Direction':
        if (callsQueryParams.dir) {
          for (let i = 0; i < section.rows[0].items.length; i++) {
            const item = section.rows[0].items[i] as DirectionItem
            item.isSelected = callsQueryParams.dir.includes(item.data)
          }
        }
        break
      case 'Outcome':
        if (callsQueryParams.miss !== undefined) {
          const missedOutcomeItem = section.rows[0].items[0] as OutcomeItem
          const answeredOutcomeItem = section.rows[0].items[1] as OutcomeItem
          missedOutcomeItem.isSelected = Number(callsQueryParams.miss) == 1
          answeredOutcomeItem.isSelected = Number(callsQueryParams.miss) == 0
        }
        break
      case 'Statuses':
        if (callsQueryParams.status) {
          for (let i = 0; i < section.rows[0].items.length; i++) {
            const item = section.rows[0].items[i] as StatusItem
            item.isSelected = callsQueryParams.status.includes(item.data![0])
          }
        }
        break
      case 'Sentiment':
        if (callsQueryParams.agent_sentiment) {
          const selectedSentiment = callsQueryParams.agent_sentiment
            .split(',')
            .map((sentiment) => sentiment.toLowerCase())
          const agentRow = section.rows.find((row) => row.title === 'Office')
          if (agentRow) {
            const items = agentRow.items as SentimentItem[]
            items.forEach((item) => {
              item.isSelected = selectedSentiment.includes(
                item.data.toLowerCase(),
              )
            })
          }
        }

        if (callsQueryParams.customer_sentiment) {
          const selectedSentiment = callsQueryParams.customer_sentiment
            .split(',')
            .map((sentiment) => sentiment.toLowerCase())
          const callerRow = section.rows.find((row) => row.title === 'Patient')
          if (callerRow) {
            const items = callerRow.items as SentimentItem[]
            items.forEach((item) => {
              item.isSelected = selectedSentiment!.includes(item.data)
            })
          }
        }
        break
      case 'Call Traits':
        if (callsQueryParams.vmr !== undefined) {
          const voicemailItem = section.rows[0].items[0] as CallTraitItem
          voicemailItem.isSelected = Boolean(callsQueryParams.vmr)
        }

        if (callsQueryParams.trnsfr !== undefined) {
          const externallyTransferredItem = section.rows[0]
            .items[1] as CallTraitItem
          externallyTransferredItem.isSelected = Boolean(
            callsQueryParams.trnsfr,
          )
        }
        break
      case 'Duration':
        if (callsQueryParams.dur_gte) {
          for (let i = 0; i < section.rows[0].items.length; i++) {
            const item = section.rows[0].items[i] as DurationItem
            item.isSelected = Number(callsQueryParams.dur_gte) == item.data
          }
        }

        if (callsQueryParams.dur_lt) {
          for (let i = 0; i < section.rows[1].items.length; i++) {
            const item = section.rows[1].items[i] as DurationItem
            item.isSelected = Number(callsQueryParams.dur_lt) == item.data
          }
        }
        break
      case 'User':
        if (pbxUsers?.length) {
          const usersParam = `${callsQueryParams.u}`
          const userIds = usersParam?.split(',')
          section.rows[0].items =
            pbxUsers?.map(
              (u) =>
                ({
                  title: u.full_name ?? 'Unknown User',
                  data: u.id,
                  isDisabled: false,
                  isSelected: userIds.includes(String(u.id)),
                }) as UserItem,
            ) ?? []
        }
        break
      default:
        break
    }
  })

  return { filterData }
}

export const tryGetSelectedFiltersFromFilterSections = (
  filterSections: FilterSection[],
): SelectedFilterData[] => {
  const selectedFilters: SelectedFilterData[] = []
  for (const section of filterSections) {
    const sectionTitle = section.title || ''
    for (const row of section.rows) {
      const rowTitle = row.title || ''
      for (const item of row.items) {
        const itemTitle = item.title || ''
        if (item.isSelected) {
          selectedFilters.push({
            sectionTitle,
            rowTitle,
            itemTitle,
            item,
          })
        }
      }
    }
  }

  return selectedFilters
}

export const tryGetFilterSectionsFromSelectedFilters = (
  selectedFilterData: SelectedFilterData[],
  callAnalyticsEnabled?: boolean,
) => {
  const filterSections = getDefaultFilterData(callAnalyticsEnabled)

  selectedFilterData.forEach((filter) => {
    const { sectionTitle, rowTitle, itemTitle } = filter

    const section = filterSections.find((s) => s.title === sectionTitle)
    if (!section) return

    const row = rowTitle
      ? section.rows.find((r) => r.title === rowTitle)
      : section.rows[0] // Assume first row if rowTitle is undefined
    if (!row) return

    const filterItem = row.items.find((i) => i.title === itemTitle)
    if (!filterItem) return

    filterItem.isSelected = true
  })

  return filterSections
}
